import { Link, useLoaderData, useNavigate } from 'react-router';
import { Heading2 } from '../../components/headings/index';
import useLevelSearch from '../../hooks/useLevelSearch';
import { routes } from '../../routes/route-definitions';
import type { FullLevel } from '../../api/types/compounds/FullLevel';
import WarningBox from '../../components/message/WarningBox';
import { LevelLengths } from '../level/types/LevelMeta';
import { FormInputDescription, FormInputLabel } from '../../components/form/index';
import { NumberInput, URLInput } from '../../components/shared/input/Input';
import Select from '../../components/shared/input/Select';
import Checkbox from '../../components/input/CheckBox';
import { PrimaryButton } from '../../components/ui/buttons/index';
import { Device } from '../../api/core/enums/device.enum';
import { useApp } from '../../context/app/useApp';
import { useMemo, useState, type SubmitEvent } from 'react';
import { validateIntChange } from '../../utils/validators/validateIntChange';
import { validateLink } from '../../utils/validators/validateLink';
import SegmentedButtonGroup from '../../components/input/buttons/segmented/SegmentedButtonGroup';
import { toast } from 'react-toastify';
import useSession from '../../hooks/useSession';
import useUserSearch from '../../hooks/useUserSearch';
import { useSubmission } from '../../components/modals/useSubmission';
import { UserWithRoles } from '../../api/user/searchUsers';
import type { GetSingleSubmissionResponse } from '../../api/submissions/GetSingleSubmission';
import InlineLoadingSpinner from '../../components/ui/InlineLoadingSpinner';
import DemonFace from '../../components/shared/DemonFace';
import { DemonLogoSizes } from '../../utils/difficultyToImgSrc';
import { getTierDifficultyRange } from '../../utils/tierDifficulty';
import { useReferences } from '../references/hooks/useReferences';
import { Difficulty, LevelFlag, SubmissionStatus } from '@gdladder/shared/enums';
import Markdown from 'react-markdown';
import markdownComponents from '../../utils/markdownComponents';
import guidelines from './guidelines.md?raw';
import { useSubmitMutation } from '../admin/add-submissions/hooks/useSubmitMutation';

const MAX_TIER = parseInt(import.meta.env.VITE_MAX_TIER);
const MINIMUM_REFRESH_RATE = parseInt(import.meta.env.VITE_MINIMUM_REFRESH_RATE);

const enjoymentOptions = {
    '-1': '-',
    '0': '0 Abysmal',
    '1': '1 Appalling',
    '2': '2 Horrible',
    '3': '3 Bad',
    '4': '4 Subpar',
    '5': '5 Average',
    '6': '6 Fine',
    '7': '7 Good',
    '8': '8 Great',
    '9': '9 Amazing',
    '10': '10 Masterpiece',
};
type EnjoymentOptions = keyof typeof enjoymentOptions;

const deviceOptions: Record<Device, string> = {
    pc: 'PC',
    mobile: 'Mobile',
};

const statusOptions: Record<SubmissionStatus, string> = {
    beaten: 'Completed',
    beating: 'In progress',
    hold: 'On hold',
    dropped: 'Dropped',
    ptb: 'Plan to beat',
};

function SubmitTierDifficulty({ tier }: { tier: number }) {
    const range = getTierDifficultyRange(tier);
    const { data: references } = useReferences();
    const tierReferences = references?.filter((reference) => reference.Tier === tier);

    if (!range) return null;

    const rangeText = range.maxTier === MAX_TIER ? `${range.minTier}+` : `${range.minTier}-${range.maxTier}`;

    return (
        <div className='space-y-1 text-sm'>
            <div className='flex items-center gap-2 text-theme-200'>
                <DemonFace diff={range.difficulty} size={DemonLogoSizes.TINY} />
                <span>
                    Tier {tier} is {range.name === 'Hard Demon' || range.name === 'Medium Demon' ? 'a' : 'an'}{' '}
                    <b>{range.name}</b> tier
                    <span className='mx-2 text-theme-400'>-</span>
                    <span className='text-theme-300'>Range: {rangeText}</span>
                </span>
            </div>
            {tierReferences && (
                <div className='wrap-break-word text-theme-300'>
                    <span className='font-bold'>References: </span>
                    {tierReferences.length === 0 && <span>No references for this tier yet.</span>}
                    {tierReferences.map((reference, index) => (
                        <span key={reference.LevelID}>
                            {index > 0 && <span className='mx-1 text-theme-400'>-</span>}
                            <Link to={`/level/${reference.LevelID}`} className='link'>
                                {reference.Level.Meta.Name}
                            </Link>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

export function SubmitPage() {
    const level = useLoaderData<FullLevel | null>();
    const session = useSession();
    const userID = session.user?.ID;
    const { data: existingSubmission, status } = useSubmission(level?.ID ?? 0, userID, {
        refetchOnWindowFocus: false,
    });

    if (status === 'pending') return <InlineLoadingSpinner />;

    return <SubmitPagePresenter submission={existingSubmission} key={existingSubmission?.ID} />;
}

interface SubmitPagePresenterProps {
    submission?: GetSingleSubmissionResponse;
}

function SubmitPagePresenter({ submission: existingSubmission }: SubmitPagePresenterProps) {
    const level = useLoaderData<FullLevel | null>();
    const navigate = useNavigate();
    const { SearchBox: LevelSearchBox } = useLevelSearch('submitLevelSearchBox', {
        onLevel(level) {
            if (level) void navigate(routes.submit.level.href(level.id));
        },
        defaultLevel: level?.ID ?? undefined,
    });

    const app = useApp();
    const [tier, setTier] = useState(existingSubmission?.Rating ?? null);
    const [enjoymentKey, setEnjoymentKey] = useState<EnjoymentOptions>(
        existingSubmission?.Enjoyment !== null && existingSubmission?.Enjoyment !== undefined
            ? (existingSubmission.Enjoyment.toString() as EnjoymentOptions)
            : '-1',
    );
    const [deviceKey, setDeviceKey] = useState(existingSubmission?.Device ?? app.defaultDevice ?? Device.PC);
    const [statusKey, setStatusKey] = useState(SubmissionStatus.BEATEN);
    const [refreshRate, setRefreshRate] = useState<number>(
        existingSubmission?.RefreshRate ?? app.defaultRefreshRate ?? 60,
    );
    const [proof, setProof] = useState(existingSubmission?.Proof ?? '');
    const [isProofPrivate, setIsProofPrivate] = useState(existingSubmission?.IsProofPrivate === 1);
    const [progress, setProgress] = useState(existingSubmission?.Progress ?? 100);
    const [attempts, setAttempts] = useState<number | null>(existingSubmission?.Attempts ?? null);
    const [wasSolo, setWasSolo] = useState(existingSubmission?.IsSolo === 1);
    const [readGuidelines, setReadGuidelines] = useState(false);

    const [r1] = useState(() => Math.random());
    const [r2] = useState(() => Math.random());
    const [randomAttempts] = useState(
        ((x: number | null) => {
            if (!x) x = r1 + 4.5;
            return 15 * x ** 2 + 200 + (r2 * 2 - 1) * x ** 0.5 * 100;
        })(level?.Rating ?? 1),
    );

    const [secondPlayer, setSecondPlayer] = useState<UserWithRoles>();
    const secondPlayerSearch = useUserSearch({ ID: 'secondPlayerSubmit', maxUsersOnList: 2, onUser: setSecondPlayer });
    const validTier = Number.isInteger(tier) && tier !== null && tier >= 1 && tier <= MAX_TIER ? tier : null;
    const isTwoPlayerLevel = level?.Meta.IsTwoPlayer === 1 && !(level.flags & LevelFlag.IS_MINIGAME);

    function onBlur(e: React.FocusEvent<HTMLInputElement>) {
        const newVal = validateIntChange(e.target.value);
        setRefreshRate(newVal ?? app.defaultRefreshRate ?? 60);
    }

    const requiresProof = useMemo(() => {
        if (statusKey !== SubmissionStatus.BEATEN) return false;
        const levelRating =
            (level?.Meta.IsTwoPlayer && !wasSolo ? level.TwoPlayerRating : level?.Rating) ?? level?.DefaultRating ?? 1;
        if (Math.round(levelRating) >= 21) return true;
        return (tier !== null && tier >= 21) || level?.Meta.Difficulty === Difficulty.EXTREME;
    }, [
        level?.DefaultRating,
        level?.Meta.Difficulty,
        level?.Meta.IsTwoPlayer,
        level?.Rating,
        level?.TwoPlayerRating,
        statusKey,
        tier,
        wasSolo,
    ]);

    function handleStatusChange(status: SubmissionStatus) {
        setStatusKey(status);
        if (status === SubmissionStatus.BEATEN) {
            setProgress(100);
        } else if (status === SubmissionStatus.PLAN_TO_BEAT) {
            setTier(null);
            setEnjoymentKey('-1');
            setProgress(0);
            setAttempts(null);
        } else {
            setProgress(Math.min(Math.max(progress, 1), 99));
        }
    }

    function handleProgressChange(value: number | null) {
        if (value === null) return;
        const p = Math.min(Math.max(value, 0), 100);
        setProgress(p);
        if (p === 100) {
            setStatusKey(SubmissionStatus.BEATEN);
        } else if (p === 0) {
            setStatusKey(SubmissionStatus.PLAN_TO_BEAT);
            setTier(null);
            setEnjoymentKey('-1');
            setAttempts(null);
        } else if (statusKey === SubmissionStatus.PLAN_TO_BEAT || statusKey === SubmissionStatus.BEATEN) {
            setStatusKey(SubmissionStatus.BEATING);
        }
    }

    const submitMutation = useSubmitMutation();

    function submitForm(e: SubmitEvent) {
        e.preventDefault();
        if (!level) return toast.error('No level selected!');

        let submittedTier = tier;
        let submittedEnjoyment = enjoymentKey === '-1' ? null : parseInt(enjoymentKey);
        let submittedProof = proof || null;
        let submittedProgress = progress;
        let submittedAttempts = attempts;

        if (statusKey === SubmissionStatus.PLAN_TO_BEAT) {
            submittedTier = null;
            submittedEnjoyment = null;
            submittedProof = null;
            submittedProgress = 0;
            submittedAttempts = null;
        }

        if (submittedTier !== null) {
            if (submittedTier < 1 || submittedTier > MAX_TIER) {
                return toast.error(`Rating must be between 1 and ${MAX_TIER}!`);
            }

            if (submittedTier >= 21 && !submittedProof) {
                return toast.error('Proof is required if you want to rate a level 21 or higher!');
            }
        }

        if (submittedAttempts && submittedAttempts <= 0) {
            return toast.error('Attempt count must be a number greater than 0!');
        }

        submitMutation.mutate({
            levelID: level.ID,
            rating: submittedTier,
            enjoyment: submittedEnjoyment,
            refreshRate: refreshRate,
            device: deviceKey,
            proof: submittedProof,
            isProofPrivate: isProofPrivate,
            progress: submittedProgress,
            status: statusKey,
            attempts: submittedAttempts ?? undefined,
            isSolo: wasSolo,
            secondPlayerID: secondPlayer?.ID,
        });
    }

    return (
        <div className='grow container mx-auto p-4'>
            <div className='max-md:flex flex-col grid grid-cols-2 bg-theme-800 border border-theme-500/20 round:rounded-xl p-4 gap-8'>
                <div>
                    <Heading2>Submit</Heading2>
                    {LevelSearchBox}
                    {level && (
                        <>
                            <form
                                onSubmit={submitForm}
                                autoCorrect='off'
                                autoCapitalize='off'
                                spellCheck='false'
                                className='grid grid-cols-1 gap-4 relative mt-4 sm:grid-cols-2'
                            >
                                {level.Meta.Length === LevelLengths.PLATFORMER && (
                                    <WarningBox>
                                        Platformer submissions are currently restricted; it's not possible to vote for
                                        tiers yet!
                                    </WarningBox>
                                )}
                                <div>
                                    <FormInputLabel htmlFor='submitRating'>Tier</FormInputLabel>
                                    <NumberInput
                                        id='submitRating'
                                        value={tier}
                                        onChange={setTier}
                                        inputMode='numeric'
                                        min={1}
                                        max={MAX_TIER}
                                        autoFocus
                                        disabled={level.Meta.Length === LevelLengths.PLATFORMER}
                                    />
                                </div>
                                <div>
                                    <FormInputLabel>Enjoyment</FormInputLabel>
                                    <Select
                                        id='submitEnjoyment'
                                        options={enjoymentOptions}
                                        activeKey={enjoymentKey}
                                        onChange={setEnjoymentKey}
                                        zIndex={1030}
                                    />
                                </div>
                                {validTier !== null && (
                                    <div className='-mt-2 sm:col-span-2'>
                                        <SubmitTierDifficulty tier={validTier} />
                                    </div>
                                )}
                                <div>
                                    <FormInputLabel htmlFor='submitRefreshRate'>
                                        FPS{' '}
                                        {statusKey !== SubmissionStatus.PLAN_TO_BEAT && (
                                            <span className='text-red-600'>*</span>
                                        )}
                                    </FormInputLabel>
                                    <NumberInput
                                        id='submitRefreshRate'
                                        value={refreshRate}
                                        onChange={(e) => setRefreshRate(e ?? 60)}
                                        onBlur={onBlur}
                                        inputMode='numeric'
                                        min={MINIMUM_REFRESH_RATE}
                                        max={960}
                                        required={statusKey !== SubmissionStatus.PLAN_TO_BEAT}
                                    />
                                    <p className='text-sm text-gray-400'>At least {MINIMUM_REFRESH_RATE}fps</p>
                                </div>
                                <div>
                                    <FormInputLabel htmlFor='submitDevice'>Device</FormInputLabel>
                                    <SegmentedButtonGroup
                                        activeKey={deviceKey}
                                        onSetActive={setDeviceKey}
                                        options={deviceOptions}
                                    />
                                </div>
                                <div className='sm:col-span-2'>
                                    <FormInputLabel htmlFor='submitProof'>
                                        Proof {requiresProof && <span className='text-red-600'>*</span>}{' '}
                                        <a href='/about#proof' target='_blank'>
                                            <i className='bx bx-info-circle' />
                                        </a>
                                    </FormInputLabel>
                                    <URLInput
                                        id='submitProof'
                                        value={proof}
                                        onChange={(e) => setProof(e.target.value)}
                                        invalid={!validateLink(proof) && (requiresProof || proof !== '')}
                                        required={requiresProof}
                                        spellCheck={false}
                                    />
                                    <label className='flex items-center gap-1'>
                                        <Checkbox
                                            checked={isProofPrivate}
                                            onChange={(e) => setIsProofPrivate(e.target.checked)}
                                        />
                                        Private
                                    </label>
                                </div>
                                {level.Meta.Length !== LevelLengths.PLATFORMER && (
                                    <div>
                                        <FormInputLabel>Percent</FormInputLabel>
                                        <NumberInput
                                            value={progress}
                                            onChange={(e) => handleProgressChange(e)}
                                            min={0}
                                            max={100}
                                            inputMode='numeric'
                                            placeholder='100'
                                        />
                                        <FormInputDescription>
                                            Optional, defaults to 100. Will not affect the level rating if set to less
                                            than 100.
                                        </FormInputDescription>
                                    </div>
                                )}
                                <div>
                                    <FormInputLabel>Attempts</FormInputLabel>
                                    <NumberInput
                                        value={attempts}
                                        onChange={setAttempts}
                                        min={1}
                                        inputMode='numeric'
                                        placeholder={randomAttempts.toFixed()}
                                    />
                                    <FormInputDescription>Optional.</FormInputDescription>
                                </div>
                                <div className='sm:col-span-2'>
                                    <FormInputLabel htmlFor='submitStatus'>Status</FormInputLabel>
                                    <div className='max-2xl:text-xs'>
                                        <SegmentedButtonGroup
                                            activeKey={statusKey}
                                            onSetActive={handleStatusChange}
                                            options={statusOptions}
                                        />
                                    </div>
                                </div>
                                {isTwoPlayerLevel && (
                                    <div className='sm:col-span-2'>
                                        <label className='flex items-center gap-2 mb-2'>
                                            <Checkbox
                                                checked={wasSolo}
                                                onChange={(e) => setWasSolo(e.target.checked)}
                                            />
                                            Solo completion
                                        </label>
                                        {!wasSolo && (
                                            <div>
                                                <p>The second player:</p>
                                                {secondPlayerSearch.SearchBox}
                                                <p className='text-sm text-gray-400'>
                                                    If the person you beat this level with doesn't have an account,
                                                    leave this blank
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className='sm:col-span-2'>
                                    <label className='flex items-center gap-2 mb-2'>
                                        <Checkbox
                                            checked={readGuidelines}
                                            onChange={(e) => setReadGuidelines(e.target.checked)}
                                            required
                                        />
                                        <p>
                                            I have read and understood all{' '}
                                            <a
                                                href='/about#guidelines'
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                className='underline-t'
                                            >
                                                rating guidelines
                                            </a>
                                            .
                                        </p>
                                    </label>
                                </div>
                                <PrimaryButton
                                    className='mt-4 sm:col-span-2'
                                    size='md'
                                    type='submit'
                                    disabled={!readGuidelines}
                                >
                                    {existingSubmission === undefined ? 'Submit' : 'Edit'}
                                </PrimaryButton>
                            </form>
                        </>
                    )}
                </div>
                <section>
                    <Markdown components={markdownComponents} children={guidelines} />
                </section>
            </div>
        </div>
    );
}
